/**
 * Merging the phone layout's chart replies into what it already holds, and
 * the chart ranges (6h / 24h / 3d / All).
 *
 * The charts call (poursAPI.getLocationChartSeries) asks for a list of
 * series and, with `since` (epoch ms, exclusive), only the points after it.
 * The reply's since_applied says what it did:
 *  - null or absent: full lists (an older backend always replies so), and
 *    the requested series replace what is held;
 *  - a number: only newer points, appended to each requested series (and to
 *    each differential partner) after the newest point held, so a point the
 *    cache already has is never added twice.
 * Fields that are not series come from the reply, except those it lists in
 * `carried` and those the server works out only with a series the request
 * left out (FIELDS_WITH_SERIES: the plain 'ea' form lists none as carried),
 * both kept from the cache, and `current`, which is merged per series
 * (mergeCurrent): the server fills each part of it only from the series of
 * the same request.
 *
 * Pure functions (no React, no API calls).
 */
import type { ChartDataResponse, ChartSeriesKey, CurrentValues } from '../../../services/pours';

/** Every series of the charts call, in the order the call lists them. */
export const ALL_SERIES: ReadonlyArray<ChartSeriesKey> = ['temp', 'tank', 'eva', 'ea', 'diff'];

/** The reply field of each list series ('diff' is difftemp, per partner). */
const LIST_FIELD = {
  temp: 'temp',
  tank: 'tankTemp',
  eva: 'eva',
  ea: 'ea',
} as const;

type ListSeries = keyof typeof LIST_FIELD;

/** Reply fields that hold series. */
const SERIES_FIELDS = new Set<string>(['temp', 'tankTemp', 'eva', 'ea', 'difftemp']);
/** Reply fields about the reply itself, never kept with the data. */
const REPLY_FIELDS = new Set<string>(['since_applied', 'carried']);
/** Reply fields the server works out only when the request has the series:
 *  a reply without it keeps what is held, listed in `carried` or not. */
const FIELDS_WITH_SERIES: ReadonlyArray<[ChartSeriesKey, ReadonlyArray<string>]> = [
  ['diff', ['temp_differential_locations']],
  [
    'ea',
    ['progress_bar', 'reading_interval_issues', 'reading_interval_issue_count', 'grade_strength_time', 'grade_outside_range'],
  ],
];

/** One point: [time (epoch ms), value, ...]. */
type Point = ReadonlyArray<number | null>;

const finite = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/** The time of a point, or null when it has none. */
export const pointTime = (point: Point | null | undefined): number | null =>
  point ? finite(point[0]) : null;

/** The newest point time of a list (a loop: lists can be long), or null. */
export const newestTime = (points: ReadonlyArray<Point> | null | undefined): number | null => {
  let newest: number | null = null;
  (points ?? []).forEach((point) => {
    const t = pointTime(point);
    if (t !== null && (newest === null || t > newest)) newest = t;
  });
  return newest;
};

/** The newest point time of each differential partner that has points. */
const newestByPartner = (difftemp: Record<string, number[][]> | null | undefined): number[] =>
  Object.values(difftemp ?? {})
    .map((points) => newestTime(points))
    .filter((t): t is number => t !== null);

/**
 * The newest point held per series. For 'diff' it is the earliest of the
 * partners' newest points, so a delta from it misses no partner's points.
 * Null when the series holds no point.
 */
export const newestBySeries = (
  data: Partial<ChartDataResponse> | null | undefined,
): Record<ChartSeriesKey, number | null> => {
  const partners = newestByPartner(data?.difftemp);
  return {
    temp: newestTime(data?.temp),
    tank: newestTime(data?.tankTemp),
    eva: newestTime(data?.eva),
    ea: newestTime(data?.ea),
    diff: partners.length > 0 ? Math.min(...partners) : null,
  };
};

/**
 * The delta cursor of the differentials: the earliest newest point of the
 * partners still reporting, those within `lagMs` of the newest partner. A
 * since reply leaves out a partner with no new points, so a silent partner
 * would otherwise hold every poll back at its last point; points it sends
 * late come with the next full reload. Null when no partner has a point.
 */
export const diffCursor = (
  difftemp: Record<string, number[][]> | null | undefined,
  lagMs: number,
): number | null => {
  const partners = newestByPartner(difftemp);
  if (partners.length === 0) return null;
  const newest = Math.max(...partners);
  return Math.min(...partners.filter((t) => t >= newest - lagMs));
};

/** The newest point of any of `series` (e.g. the "Newest 4 min ago" of a
 *  chart), or null. */
export const newestOf = (
  data: Partial<ChartDataResponse> | null | undefined,
  series: ReadonlyArray<ChartSeriesKey> = ALL_SERIES,
): number | null => {
  const newest = newestBySeries(data);
  const partners = newestByPartner(data?.difftemp);
  let latest: number | null = null;
  series.forEach((s) => {
    const t = s === 'diff' ? (partners.length > 0 ? Math.max(...partners) : null) : newest[s];
    if (t !== null && (latest === null || t > latest)) latest = t;
  });
  return latest;
};

/**
 * `incoming` points newer than the newest point held, appended to `held`.
 * Points at or before it are dropped (the reply can repeat the boundary
 * point). The same array comes back when nothing is new.
 */
export const appendNewer = <P extends Point>(
  held: ReadonlyArray<P> | null | undefined,
  incoming: ReadonlyArray<P> | null | undefined,
): P[] => {
  const base = (held ?? []) as P[];
  if (!incoming || incoming.length === 0) return base;
  const last = newestTime(base);
  const fresh = incoming.filter((point) => {
    const t = pointTime(point);
    return t !== null && (last === null || t > last);
  });
  return fresh.length > 0 ? [...base, ...fresh] : base;
};

/** Each partner's new points appended; partners the reply leaves out keep
 *  theirs, a new partner is added. */
const appendDifferentials = (
  held: Record<string, number[][]> | null | undefined,
  incoming: Record<string, number[][]> | null | undefined,
): Record<string, number[][]> => {
  const out: Record<string, number[][]> = { ...(held ?? {}) };
  Object.entries(incoming ?? {}).forEach(([partner, points]) => {
    out[partner] = appendNewer(out[partner], points);
  });
  return out;
};

/** Every series field present: [] for a list, {} for the differentials, so
 *  a chart built from it never meets a missing list. */
export const withSeriesDefaults = (data: Partial<ChartDataResponse>): ChartDataResponse => ({
  ...data,
  temp: data.temp ?? [],
  tankTemp: data.tankTemp ?? [],
  eva: data.eva ?? [],
  ea: data.ea ?? [],
  difftemp: data.difftemp ?? {},
} as ChartDataResponse);

/** The parts of `current` the server works out from a series other than
 *  the temperatures (the rest come with 'temp'). The first field of each
 *  says whether a delta reply had new points for it. */
const CURRENT_FROM_SERIES: ReadonlyArray<[ChartSeriesKey, ReadonlyArray<keyof CurrentValues>]> = [
  ['eva', ['evaporation']],
  ['ea', ['ea', 'strength', 'strength_reading']],
];

/**
 * The held `current` with a reply's merged in. The server fills the
 * temperatures part only when 'temp' is requested (else it lists `current`
 * as carried), evaporation only with 'eva' and maturity and strength only
 * with 'ea'; in a delta, a part of a requested series that comes back empty
 * had no new points. Each part comes from the reply only when it was filled
 * there, else from what is held. The held object comes back when nothing
 * changed.
 */
export const mergeCurrent = (
  held: CurrentValues | null | undefined,
  reply: CurrentValues | null | undefined,
  requested: ReadonlyArray<ChartSeriesKey>,
  full: boolean,
): CurrentValues | null | undefined => {
  if (held == null) return reply;
  if (reply == null) return held;
  const withTemp = requested.includes('temp');
  const out = { ...(withTemp ? reply : held) } as Record<string, unknown>;
  let changed = withTemp;
  CURRENT_FROM_SERIES.forEach(([series, fields]) => {
    const fromReply = requested.includes(series) && (full || reply[fields[0]] != null);
    const source = (fromReply ? reply : held) as unknown as Record<string, unknown>;
    fields.forEach((field) => {
      if (out[field] === source[field]) return;
      out[field] = source[field];
      changed = true;
    });
  });
  return changed ? (out as unknown as CurrentValues) : held;
};

export interface MergeChartReplyOptions {
  /** Replace the requested series whatever since_applied says: the request
   *  was not a delta of what is held (a first or windowed load). */
  replace?: boolean;
}

/**
 * The held chart data with a reply merged in (see the module comment).
 * Only the `requested` series change; the others keep what is held. No
 * held data, or `replace`, replaces the requested series.
 */
export const mergeChartReply = (
  cached: ChartDataResponse | null | undefined,
  reply: ChartDataResponse,
  requested: ReadonlyArray<ChartSeriesKey>,
  { replace = false }: MergeChartReplyOptions = {},
): ChartDataResponse => {
  const full = replace || !cached || reply.since_applied == null;
  const held = (cached ?? {}) as Record<string, unknown>;
  const incoming = reply as unknown as Record<string, unknown>;
  const out: Record<string, unknown> = { ...held };
  const carried = new Set(reply.carried ?? []);
  FIELDS_WITH_SERIES.forEach(([series, fields]) => {
    if (!requested.includes(series)) fields.forEach((field) => carried.add(field));
  });

  Object.keys(incoming).forEach((key) => {
    if (SERIES_FIELDS.has(key) || REPLY_FIELDS.has(key) || key === 'current') return;
    if (carried.has(key) && key in held) return;
    out[key] = incoming[key];
  });
  if ('current' in incoming) {
    out.current = mergeCurrent(cached?.current, reply.current, requested, full);
  }

  requested.forEach((series) => {
    if (series === 'diff') {
      out.difftemp = full
        ? { ...(reply.difftemp ?? {}) }
        : appendDifferentials(cached?.difftemp, reply.difftemp);
      return;
    }
    const field = LIST_FIELD[series as ListSeries];
    const points = incoming[field] as Point[] | undefined;
    out[field] = full ? points ?? [] : appendNewer(held[field] as Point[] | undefined, points);
  });

  REPLY_FIELDS.forEach((key) => {
    delete out[key];
  });
  return withSeriesDefaults(out as Partial<ChartDataResponse>);
};

// ---------------------------------------------------------------------------
// Chart ranges
// ---------------------------------------------------------------------------

/** The phone charts' ranges: the last 6 hours, 24 hours, 3 days, or all. */
export type ChartRangeKey = '6h' | '24h' | '3d' | 'all';

export const CHART_RANGE_KEYS: ReadonlyArray<ChartRangeKey> = ['6h', '24h', '3d', 'all'];

const HOUR_MS = 60 * 60 * 1000;

const RANGE_MS: Record<ChartRangeKey, number | null> = {
  '6h': 6 * HOUR_MS,
  '24h': 24 * HOUR_MS,
  '3d': 72 * HOUR_MS,
  all: null,
};

/** The length of a range in ms; null for All (the whole history). */
export const chartRangeMs = (range: ChartRangeKey): number | null => RANGE_MS[range];

/** Readings spanning more than this open on 24h rather than All. */
export const LONG_SPAN_MS = 30 * HOUR_MS;

/**
 * The range a phone chart opens on: 24h when the readings span more than
 * 30 hours (from `startMs`, e.g. the logging start, to `endMs`, the newest
 * reading or now), else All. All when the start is unknown.
 */
export const defaultChartRange = (
  startMs: number | null | undefined,
  endMs: number | null | undefined,
): ChartRangeKey => {
  const start = finite(startMs);
  const end = finite(endMs);
  return start !== null && end !== null && end - start > LONG_SPAN_MS ? '24h' : 'all';
};
